import { Injectable, inject } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ToastNotificationService } from '@app/core/infrastructure';
import { LoggerService } from '@app/core/infrastructure';
import { AuthFacade } from '@app/store/auth/auth.facade';
import { Router } from '@angular/router';
import { NetworkStatusService } from '@app/core/infrastructure';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  private readonly logger = inject(LoggerService).scope('ErrorInterceptor');

  constructor(
    private toastNotificationService: ToastNotificationService,
    private networkStatusService: NetworkStatusService,
    private authFacade: AuthFacade,
    private router: Router
  ) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        let errorMessage = this.getErrorMessage(error);

        // Keep raw backend payload for IAP receipt validation so the client
        // can handle structured conflict codes (e.g. apple_subscription_already_linked).
        if (request.url.includes('/protected/iap/validate-receipt')) {
          return throwError(() => error);
        }
        
        // Handle 401 Unauthorized specifically
        if (error.status === 401 && !request.url.includes('/auth/')) {
          // Skip unauthorized handling for push token unregister calls during logout
          // to prevent infinite loops
          if (request.url.includes('/push-tokens/unregister')) {
            this.logger.debug('401 on push token unregister during logout, skipping auth cleanup');
            return throwError(() => new Error(errorMessage));
          }
          
          this.logger.debug('401 Unauthorized detected, handling auth cleanup');
          // Clear local authentication state and logout
          this.authFacade.logout();
          // Show notification
          this.toastNotificationService.showError('Session expired. Please log in again.');
          // Redirect to login
          this.router.navigate(['/login']);
          return throwError(() => new Error(errorMessage));
        }

        // Handle 402 Payment Required - insufficient subscription/minutes
        if (error.status === 402) {
          this.logger.debug('402 Payment Required detected, redirecting to pricing');
          // Show notification about subscription issue
          this.toastNotificationService.showError('Please upgrade your subscription or purchase additional minutes to continue.');
          // Redirect to pricing page
          this.router.navigate(['/pricing']);
          return throwError(() => new Error(errorMessage));
        }
        
        // Only show notification for other errors based on existing logic
        if (this.shouldShowNotification(error)) {
          this.toastNotificationService.showError(errorMessage);
        }

        // Always re-throw the error so components can handle it if needed
        return throwError(() => new Error(errorMessage));
      })
    );
  }

  private getErrorMessage(error: HttpErrorResponse): string {
    if (error.error instanceof ErrorEvent) {
      // Client-side or network error
      return `Network error: ${error.error.message}`;
    }

    // Server-side error
    switch (error.status) {
      case 0:
        return 'Unable to connect to the server. Please check your internet connection and try again.';
      case 400:
        return this.extractServerMessage(error) || 'Invalid request. Please check your input and try again.';
      case 401:
        return 'Session expired. Please log in again.';
      case 402:
        return 'Insufficient subscription or minutes. Please upgrade your plan or purchase additional minutes.';
      case 403:
        return 'You do not have permission to perform this action.';
      case 404:
        return 'The requested resource was not found.';
      case 409:
        return this.extractServerMessage(error) || 'There was a conflict with your request.';
      case 422:
        return this.extractServerMessage(error) || 'Invalid data provided. Please check your input.';
      case 429:
        return 'Too many requests. Please wait a moment and try again.';
      case 500:
        return 'Server error. Please try again later.';
      case 502:
      case 503:
      case 504:
        return 'Service temporarily unavailable. Please try again in a few moments.';
      default:
        return this.extractServerMessage(error) || `An unexpected error occurred (${error.status}). Please try again.`;
    }
  }

  private extractServerMessage(error: HttpErrorResponse): string | null {
    // Try to extract meaningful error message from server response
    if (error.error) {
      if (typeof error.error === 'string') {
        return error.error;
      }
      if (error.error.detail) {
        return error.error.detail;
      }
      if (error.error.message) {
        // Check if message is an object with detail property
        if (typeof error.error.message === 'object' && error.error.message.detail) {
          return error.error.message.detail;
        }
        // Otherwise treat as string
        if (typeof error.error.message === 'string') {
          return error.error.message;
        }
      }
      if (error.error.error) {
        return error.error.error;
      }
    }
    return null;
  }

  private shouldShowNotification(error: HttpErrorResponse): boolean {
    // Don't show notification for network errors (status 0) if we're offline
    // The NetworkStatusService will handle offline notifications
    if (error.status === 0 && !this.networkStatusService.isOnline) {
      return false;
    }
    
    // Don't show notification for avatar operations - let the effects handle it
    if (error.url && error.url.includes('/avatars/')) {
      return false;
    }
    
    // Show notifications for network errors (status 0), client errors (4xx except 401), and server errors (5xx)
    // 401 is handled separately above
    return error.status === 0 || 
           (error.status >= 400 && error.status < 500 && error.status !== 401) || 
           error.status >= 500;
  }
}
